import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Calendar as CalendarIcon,
  MapPin,
  Clock,
  Users,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import { Card } from '@components/common/Card';
import { Badge } from '@components/common/Badge';
import { Button } from '@components/common/Button';
import { PageHeader } from '@components/common/PageHeader';
import { mockCenters, mockTimeSlots } from '@mocks/bookings';
import { getCurrentApplicant } from '@mocks/applicants';

interface TimeSlot {
  id: number;
  date: string;
  start_time: string;
  end_time: string;
  capacity: number;
  booked: number;
  status: 'AVAILABLE' | 'FULL' | 'CLOSED';
}

export const BookingCenter: React.FC = () => {
  const navigate = useNavigate();
  const applicant = getCurrentApplicant();
  const [selectedCenter, setSelectedCenter] = useState(mockCenters[0]);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [viewMode, setViewMode] = useState<'month' | 'week'>('week');
  const [isBooking, setIsBooking] = useState(false);

  // Calendar logic
  const today = new Date();
  const currentMonth = selectedDate.getMonth();
  const currentYear = selectedDate.getFullYear();

  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();

  const handlePrevMonth = () => {
    setSelectedDate(new Date(currentYear, currentMonth - 1, 1));
  };

  const handleNextMonth = () => {
    setSelectedDate(new Date(currentYear, currentMonth + 1, 1));
  };

  const handleDateClick = (day: number) => {
    const newDate = new Date(currentYear, currentMonth, day);
    setSelectedDate(newDate);
    setSelectedSlot(null);
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatShortDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const isSameDay = (date1: Date, date2: Date): boolean => {
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    );
  };

  const isPastDate = (date: Date): boolean => {
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);
    return compareDate < todayDate;
  };

  // Get available slots for selected date
  const availableSlots = useMemo(() => {
    const dateStr = selectedDate.toISOString().split('T')[0];
    return mockTimeSlots
      .filter((slot) => slot.date === dateStr && slot.center_id === selectedCenter.id)
      .map((slot) => ({
        id: slot.id,
        date: slot.date,
        start_time: slot.start_time,
        end_time: slot.end_time,
        capacity: slot.capacity,
        booked: slot.booked,
        status: slot.status,
      }));
  }, [selectedDate, selectedCenter]);

  const handleBooking = async () => {
    if (!selectedSlot) return;
    
    setIsBooking(true);
    
    // Mock API call delay
    await new Promise((resolve) => setTimeout(resolve, 1500));
    
    const bookingRef = `BK-${new Date().getFullYear()}${String(new Date().getMonth() + 1).padStart(2, '0')}${String(new Date().getDate()).padStart(2, '0')}-${Math.random().toString(36).substr(2, 8).toUpperCase()}`;
    
    console.log('✅ Mock: Booking created', {
      booking_reference: bookingRef,
      applicant_id: applicant.id,
      center_id: selectedCenter.id,
      slot_id: selectedSlot.id,
      date: selectedSlot.date,
      time: `${selectedSlot.start_time} - ${selectedSlot.end_time}`,
      status: 'CONFIRMED',
    });
    
    toast.success(
      `Appointment booked successfully! Reference: ${bookingRef}`,
      { duration: 5000 }
    );
    
    setIsBooking(false);
    
    // Redirect to appointments page after 1.5 seconds
    setTimeout(() => {
      navigate('/appointments');
    }, 1500);
  };

  const renderCalendar = () => {
    const days = [];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    // Day headers
    const dayHeaders = dayNames.map((day) => (
      <div key={day} className="text-center text-xs font-semibold text-gray-600 py-2">
        {day}
      </div>
    ));

    // Empty cells for alignment
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(
        <div key={`empty-${i}`} className="p-2 text-center">
          <div className="w-10 h-10"></div>
        </div>
      );
    }

    // Calendar days
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth, day);
      const isToday = isSameDay(date, today);
      const isSelected = isSameDay(date, selectedDate);
      const isPast = isPastDate(date);
      const dateStr = date.toISOString().split('T')[0];
      
      // Check if slots available for this date
      const hasSlots = mockTimeSlots.some(
        (slot) => slot.date === dateStr && slot.center_id === selectedCenter.id && slot.status === 'AVAILABLE'
      );

      days.push(
        <div key={day} className="p-1">
          <button
            onClick={() => !isPast && handleDateClick(day)}
            disabled={isPast}
            className={`
              w-full aspect-square rounded-lg text-sm font-medium transition-all
              ${
                isSelected
                  ? 'bg-ncaa-primary text-white ring-2 ring-ncaa-primary ring-offset-2'
                  : isToday
                  ? 'bg-blue-50 text-ncaa-primary border-2 border-ncaa-primary'
                  : isPast
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'hover:bg-gray-100 text-gray-700'
              }
              relative
            `}
          >
            {day}
            {hasSlots && !isPast && (
              <span className="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-green-500 rounded-full"></span>
            )}
          </button>
        </div>
      );
    }

    return (
      <div>
        <div className="grid grid-cols-7 gap-1 mb-2">{dayHeaders}</div>
        <div className="grid grid-cols-7 gap-1">{days}</div>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <PageHeader
        title="Booking Center"
        description="Schedule your appointment for license processing and biometric capture"
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Center Selection & Calendar */}
        <div className="lg:col-span-2 space-y-6">
          {/* Center Selection */}
          <Card title="Select Processing Center" icon={<MapPin className="h-5 w-5" />}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {mockCenters.map((center) => (
                <button
                  key={center.id}
                  onClick={() => {
                    setSelectedCenter(center);
                    setSelectedSlot(null);
                  }}
                  className={`
                    text-left p-4 rounded-lg border-2 transition-all
                    ${
                      selectedCenter.id === center.id
                        ? 'border-ncaa-primary bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }
                  `}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">{center.name}</h4>
                      <p className="text-sm text-gray-600 mt-1">{center.address}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {center.city}, {center.state}
                      </p>
                    </div>
                    {selectedCenter.id === center.id && (
                      <CheckCircle className="h-5 w-5 text-ncaa-primary flex-shrink-0" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </Card>

          {/* Calendar */}
          <Card
            title="Select Date"
            icon={<CalendarIcon className="h-5 w-5" />}
            actions={
              <div className="flex items-center gap-4">
                <div className="text-sm font-medium text-gray-700">
                  {selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={handlePrevMonth}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleNextMonth}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            }
          >
            {renderCalendar()}
            <div className="mt-4 pt-4 border-t border-gray-200 flex items-center gap-4 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-ncaa-primary"></div>
                <span className="text-gray-600">Selected</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full border-2 border-ncaa-primary"></div>
                <span className="text-gray-600">Today</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className="text-gray-600">Available</span>
              </div>
            </div>
          </Card>
        </div>

        {/* Right Column - Time Slots */}
        <div className="space-y-6">
          {/* Selected Date Info */}
          <Card>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-ncaa-primary text-white rounded-full mb-4">
                <CalendarIcon className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">{formatDate(selectedDate)}</h3>
              <p className="text-sm text-gray-500 mt-1">{selectedCenter.name}</p>
            </div>
          </Card>

          {/* Time Slots */}
          <Card title="Available Time Slots" icon={<Clock className="h-5 w-5" />}>
            {availableSlots.length === 0 ? (
              <div className="text-center py-8">
                <AlertCircle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">No slots available for this date</p>
                <p className="text-gray-400 text-xs mt-1">Please select another date</p>
              </div>
            ) : (
              <div className="space-y-3">
                {availableSlots.map((slot) => {
                  const isSelected = selectedSlot?.id === slot.id;
                  const isAvailable = slot.status === 'AVAILABLE' && slot.booked < slot.capacity;
                  const slotsLeft = slot.capacity - slot.booked;

                  return (
                    <button
                      key={slot.id}
                      onClick={() => isAvailable && setSelectedSlot(slot)}
                      disabled={!isAvailable}
                      className={`
                        w-full text-left p-4 rounded-lg border-2 transition-all
                        ${
                          isSelected
                            ? 'border-ncaa-primary bg-blue-50'
                            : isAvailable
                            ? 'border-gray-200 hover:border-ncaa-light'
                            : 'border-gray-100 bg-gray-50 cursor-not-allowed opacity-60'
                        }
                      `}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-gray-500" />
                            <span className="font-semibold text-gray-900">
                              {slot.start_time} - {slot.end_time}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            <Users className="h-3 w-3 text-gray-400" />
                            <span className="text-xs text-gray-600">
                              {slotsLeft} {slotsLeft === 1 ? 'slot' : 'slots'} left
                            </span>
                          </div>
                        </div>
                        {isSelected && <CheckCircle className="h-5 w-5 text-ncaa-primary" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </Card>

          {/* Booking Confirmation */}
          {selectedSlot && (
            <Card>
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Booking Summary</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Center:</span>
                      <span className="font-medium text-gray-900">{selectedCenter.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Date:</span>
                      <span className="font-medium text-gray-900">{formatShortDate(selectedDate)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Time:</span>
                      <span className="font-medium text-gray-900">
                        {selectedSlot.start_time} - {selectedSlot.end_time}
                      </span>
                    </div>
                  </div>
                </div>
                <Button
                  onClick={handleBooking}
                  loading={isBooking}
                  className="w-full"
                  icon={<CheckCircle className="h-4 w-4" />}
                >
                  Confirm Booking
                </Button>
                <p className="text-xs text-gray-500 text-center">
                  You will receive a confirmation email after booking
                </p>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};
